import { useCallback, useMemo, useState } from "react";
import { AttachmentRow, ResourceAttachment } from "features/Resources/Shared/attachments";
import { useFileUploads } from "features/Resources/Shared/hooks/useFileUploads";

type StagedItem = AttachmentRow & { signedId?: string; existingId?: number };

let seq = 0;
const nextKey = () => `staged-${(seq += 1)}-${Date.now()}`;

function toStaged(existing: ResourceAttachment[] | undefined): StagedItem[] {
  return (existing ?? []).map((a) => ({
    key: `existing-${a.id}`,
    filename: a.filename,
    byteSize: a.byteSize,
    status: "done" as const,
    progress: 100,
    existingId: a.id,
    url: a.url,
  }));
}

// Staged tray for explicit-Save / create forms: nothing persists until the parent
// submits newSignedIds + removedExistingIds. Required for create (no id to attach to yet).
export function useStagedAttachments(existing: ResourceAttachment[] | undefined) {
  const [staged, setStaged] = useState<StagedItem[]>(() => toStaged(existing));
  const [removedExistingIds, setRemovedExistingIds] = useState<number[]>([]);

  const stagedBytes = staged.reduce((sum, i) => sum + i.byteSize, 0);
  const uploads = useFileUploads({
    baseBytes: stagedBytes,
    onUploaded: (result) =>
      setStaged((prev) => [
        ...prev,
        { key: nextKey(), filename: result.filename, byteSize: result.byteSize, status: "done", progress: 100, signedId: result.signedId },
      ]),
  });

  const items: AttachmentRow[] = useMemo(() => [...staged, ...uploads.inFlight], [staged, uploads.inFlight]);

  const remove = useCallback(
    (key: string) => {
      if (uploads.inFlight.some((r) => r.key === key)) {
        uploads.cancel(key);
        return;
      }
      setStaged((prev) => {
        const target = prev.find((i) => i.key === key);
        if (target?.existingId) setRemovedExistingIds((ids) => [...ids, target.existingId as number]);
        return prev.filter((i) => i.key !== key);
      });
    },
    [uploads],
  );

  const newSignedIds = useMemo(
    () => staged.filter((i) => i.signedId && !i.existingId).map((i) => i.signedId as string),
    [staged],
  );

  return { items, addFiles: uploads.add, remove, newSignedIds, removedExistingIds, isUploading: uploads.isUploading };
}
